import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminLogEntry, AdminSystemHealth } from '@/types/admin'

export default function AdminSystemPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [health, setHealth] = useState<AdminSystemHealth | null>(null)
  const [logs, setLogs] = useState<AdminLogEntry[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [healthRes, logsRes] = await Promise.all([
          adminApi.systemHealth(),
          adminApi.systemLogs({ limit: 20 }),
        ])
        setHealth((healthRes as { data?: AdminSystemHealth }).data || (healthRes as AdminSystemHealth))
        setLogs((logsRes as { data?: AdminLogEntry[] }).data || (logsRes as unknown as AdminLogEntry[]))
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load system info')
      }
    }
    void load()
  }, [])

  const settings = [
    { label: 'Feature Flags', value: 'Enabled' },
    { label: 'SMS Provider', value: 'Twilio (active)' },
    { label: 'WhatsApp', value: 'Degraded' },
    { label: 'Storage', value: 'Healthy' },
  ]

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' System'
          infoText='System health, logs, and settings.'
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>System Health</h3>
          <div className='space-y-4'>
            <div>
              <p className='text-xs text-gray-500 mb-2'>Overall Status</p>
              <p className='text-base font-semibold text-green-600 capitalize'>
                {health?.status || 'Healthy'}
              </p>
            </div>
            
            <div>
              <p className='text-xs text-gray-500 mb-2'>Database</p>
              <p className='text-sm font-semibold text-gray-900 capitalize mb-1'>
                {health?.database?.status || 'Connected'}
              </p>
              {health?.database?.recordCounts && (
                <div className='text-xs text-gray-600 space-y-1'>
                  <div className='flex justify-between'>
                    <span>Users:</span>
                    <span className='font-medium'>{health.database.recordCounts.users || 0}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Organizations:</span>
                    <span className='font-medium'>{health.database.recordCounts.organizations || 0}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Couples:</span>
                    <span className='font-medium'>{health.database.recordCounts.couples || 0}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <p className='text-xs text-gray-500 mb-2'>Storage</p>
              <p className='text-sm font-semibold text-gray-900 capitalize mb-1'>
                {health?.storage?.status || 'Available'}
              </p>
              {health?.storage && (
                <div className='text-xs text-gray-600'>
                  <div className='flex justify-between'>
                    <span>Used:</span>
                    <span className='font-medium'>{health.storage.used || 'N/A'}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Available:</span>
                    <span className='font-medium'>{health.storage.available || 'N/A'}</span>
                  </div>
                </div>
              )}
            </div>

            {health?.services && (
              <div>
                <p className='text-xs text-gray-500 mb-2'>Services</p>
                <div className='text-xs text-gray-600 space-y-1'>
                  <div className='flex justify-between'>
                    <span>Authentication:</span>
                    <span className='font-medium capitalize text-green-600'>{health.services.authentication || 'operational'}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Notifications:</span>
                    <span className='font-medium capitalize text-green-600'>{health.services.notifications || 'operational'}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>File Upload:</span>
                    <span className='font-medium capitalize text-green-600'>{health.services.fileUpload || 'operational'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Logs</h3>
          <div className='h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 text-xs font-mono text-gray-700 space-y-2'>
            {(logs.length ? logs : [{ level: 'info', message: 'System boot completed' }]).map((log, idx) => (
              <div key={`${log.timestamp ?? idx}-${idx}`}>
                [{log.level?.toUpperCase()}] {log.message}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-4'>
        <h3 className='text-lg font-semibold text-gray-900'>Settings</h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-800'>
          {settings.map((item) => (
            <div key={item.label} className='p-3 rounded-lg bg-gray-50'>
              <p className='text-xs text-gray-500'>{item.label}</p>
              <p className='text-base font-semibold text-gray-900'>{item.value}</p>
            </div>
          ))}
        </div>
        <button className='px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800'>
          Update Settings
        </button>
      </div>
      {error && <p className='text-sm text-red-600'>{error}</p>}
    </div>
  )
}
